import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Avatar,
  Divider,
  Badge,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  TableRow,
  TableCell,
  tableCellClasses,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

// Icons
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatIcon from "@mui/icons-material/Chat";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// Components
import CommonTable from "../Components/Common/CommonTable";
import CustomPagination from "../Components/Common/CommonPagination";
import DataNotFound from "../Components/Common/DataNotFound";
import ButtonContained from "../Components/Common/ButtonContained";
import { showLoader } from "../Redux/Slices/loaderSlice";
import ErrorHandler from "../Utils/handler";
import { BucketURL } from "../ApiSetup/Api";

// Actions
import {
  getTicket,
  getTicketById,
  getTicketStats,
  updateTicket,
  addTicketReply,
  deleteTicket,
} from "../Redux/Actions/action";

dayjs.extend(relativeTime);

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 14,
    textAlign: "center",
    lineHeight: "1.2rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "12px !important",
    textAlign: "center",
  },
}));

const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: "16px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
  border: `1px solid ${color}30`,
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 4px 12px ${color}20`,
  },
}));

const PriorityChip = styled(Chip)(({ priority }) => {
  const colors = {
    Urgent: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
    High: { bg: "#ffedd5", color: "#ea580c", border: "#fdba74" },
    Medium: { bg: "#fef3c7", color: "#d97706", border: "#fcd34d" },
    Low: { bg: "#dcfce7", color: "#16a34a", border: "#86efac" },
  };
  const c = colors[priority] || colors.Medium;
  return {
    backgroundColor: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    fontWeight: 600,
    fontSize: "12px",
  };
});

const StatusChip = styled(Chip)(({ status }) => {
  const colors = {
    Open: { bg: "#dbeafe", color: "#2563eb" },
    Pending: { bg: "#dbeafe", color: "#2563eb" }, // Legacy status
    "In Progress": { bg: "#fef3c7", color: "#d97706" },
    Resolved: { bg: "#dcfce7", color: "#16a34a" },
    Approved: { bg: "#dcfce7", color: "#16a34a" }, // Legacy status
    Closed: { bg: "#e5e7eb", color: "#4b5563" },
    Rejected: { bg: "#fee2e2", color: "#dc2626" },
  };
  const c = colors[status] || colors.Open;
  return {
    backgroundColor: c.bg,
    color: c.color,
    fontWeight: 600,
    fontSize: "12px",
  };
});

const MessageBubble = styled(Box)(({ isAdmin }) => ({
  maxWidth: "80%",
  padding: "12px 16px",
  borderRadius: isAdmin ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
  backgroundColor: isAdmin ? "#3E4094" : "#f1f5f9",
  color: isAdmin ? "#fff" : "#1e293b",
  marginLeft: isAdmin ? "auto" : 0,
  marginRight: isAdmin ? 0 : "auto",
}));

const HelpDesk = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);

  // State
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0, urgent: 0 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    type: "All",
    priority: "All",
    category: "All",
    search: "",
  });
  const [categories] = useState([
    "All", "Payment Issue", "Registration Issue", "Document Issue", 
    "Technical Issue", "Claim Issue", "General Inquiry", "Other"
  ]);
  const [priorities] = useState(["All", "Low", "Medium", "High", "Urgent"]);
  const [statuses] = useState(["All", "Open", "In Progress", "Resolved", "Closed", "Rejected"]);

  // Fetch tickets
  const fetchTickets = async () => {
    dispatch(showLoader(true));
    try {
      const body = {
        page,
        limit: rowsPerPage,
        ...(filters.type !== "All" && { type: filters.type }),
        ...(filters.priority !== "All" && { priority: filters.priority }),
        ...(filters.category !== "All" && { category: filters.category }),
        ...(filters.search && { search: filters.search }),
      };
      const result = await dispatch(getTicket(body));
      if (ErrorHandler(result, false)) {
        setTickets(result?.payload?.data?.response || []);
        setTotalCount(result?.payload?.data?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const result = await dispatch(getTicketStats({}));
      if (ErrorHandler(result, false)) {
        setStats(result?.payload?.data?.stats || {});
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Fetch ticket details
  const fetchTicketDetails = async (ticketId) => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(getTicketById({ _id: ticketId }));
      if (ErrorHandler(result, false)) {
        setSelectedTicket(result?.payload?.data?.ticket);
        setDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching ticket details:", error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Update ticket status
  const handleUpdateStatus = async (ticketId, newStatus) => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(updateTicket({ _id: ticketId, status: newStatus }));
      if (ErrorHandler(result)) {
        toast.success("Ticket updated successfully");
        fetchTickets();
        fetchStats();
        if (selectedTicket?._id === ticketId) {
          setSelectedTicket({ ...selectedTicket, status: newStatus });
        }
      }
    } catch (error) {
      toast.error("Failed to update ticket");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Send reply
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    dispatch(showLoader(true));
    try {
      const result = await dispatch(addTicketReply({ 
        _id: selectedTicket._id, 
        message: replyMessage 
      }));
      if (ErrorHandler(result)) {
        toast.success("Reply sent successfully");
        setReplyMessage("");
        fetchTicketDetails(selectedTicket._id);
        fetchTickets();
      }
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Delete ticket
  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) return;
    
    dispatch(showLoader(true));
    try {
      const result = await dispatch(deleteTicket({ _id: ticketId }));
      if (ErrorHandler(result)) {
        toast.success("Ticket deleted successfully");
        fetchTickets();
        fetchStats();
        if (detailOpen) setDetailOpen(false);
      }
    } catch (error) {
      toast.error("Failed to delete ticket");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Effects
  useEffect(() => {
    fetchTickets();
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedTicket?.replies]);

  // Header list for table
  const headerList = [
    "No", "Ticket #", "User", "Subject", "Category", "Priority", "Status", "Date", "Actions"
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600} color="#1e293b">
          Help Desk
        </Typography>
        <IconButton onClick={() => { fetchTickets(); fetchStats(); }} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard color="#3E4094">
            <ConfirmationNumberIcon sx={{ fontSize: 32, color: "#3E4094" }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="#3E4094">{stats.total || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Total</Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard color="#2563eb">
            <PendingActionsIcon sx={{ fontSize: 32, color: "#2563eb" }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="#2563eb">{stats.open || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Open</Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard color="#d97706">
            <ChatIcon sx={{ fontSize: 32, color: "#d97706" }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="#d97706">{stats.inProgress || 0}</Typography>
              <Typography variant="caption" color="text.secondary">In Progress</Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard color="#16a34a">
            <CheckCircleIcon sx={{ fontSize: 32, color: "#16a34a" }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="#16a34a">{stats.resolved || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Resolved</Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard color="#dc2626">
            <PriorityHighIcon sx={{ fontSize: 32, color: "#dc2626" }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="#dc2626">{stats.urgent || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Urgent</Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard color="#4b5563">
            <ErrorIcon sx={{ fontSize: 32, color: "#4b5563" }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="#4b5563">{stats.closed || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Closed</Typography>
            </Box>
          </StatCard>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tickets..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Open">Open / Pending</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Resolved">Resolved</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Priority"
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            >
              {priorities.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tickets Table */}
      <CommonTable headerList={headerList} isNotSorting>
        {tickets.length > 0 ? (
          tickets.map((ticket, index) => (
            <TableRow key={ticket._id} hover sx={{ cursor: "pointer" }} onClick={() => fetchTicketDetails(ticket._id)}>
              <StyledTableCell>{(page - 1) * rowsPerPage + index + 1}</StyledTableCell>
              <StyledTableCell>
                <Typography fontWeight={600} color="primary">#{ticket.ticketNumber}</Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: "#3E4094", fontSize: 12 }}>
                    {ticket.userDetails?.fullName?.charAt(0) || "U"}
                  </Avatar>
                  <Box textAlign="left">
                    <Typography fontSize={13} fontWeight={500}>{ticket.userDetails?.fullName || "User"}</Typography>
                    <Typography fontSize={11} color="text.secondary">{ticket.userDetails?.ppskId || "-"}</Typography>
                  </Box>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Typography fontSize={13} sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {ticket.subject}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Chip label={ticket.category || "General"} size="small" variant="outlined" />
              </StyledTableCell>
              <StyledTableCell>
                <PriorityChip label={ticket.priority || "Medium"} priority={ticket.priority} size="small" />
              </StyledTableCell>
              <StyledTableCell>
                <StatusChip label={ticket.status} status={ticket.status} size="small" />
              </StyledTableCell>
              <StyledTableCell>
                <Typography fontSize={12} color="text.secondary">
                  {dayjs(ticket.createdAt).format("DD MMM YYYY")}
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  {dayjs(ticket.createdAt).fromNow()}
                </Typography>
              </StyledTableCell>
              <StyledTableCell onClick={(e) => e.stopPropagation()}>
                <Box display="flex" gap={0.5} justifyContent="center">
                  <IconButton size="small" color="primary" onClick={() => fetchTicketDetails(ticket._id)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteTicket(ticket._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </StyledTableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <StyledTableCell colSpan={9}>
              <DataNotFound
                title="No Tickets Found"
                icon={<ErrorOutlineIcon color="primary" style={{ fontSize: "3rem" }} />}
                elevation={0}
              />
            </StyledTableCell>
          </TableRow>
        )}
      </CommonTable>

      {/* Pagination */}
      {totalCount > 0 && (
        <Box mt={2}>
          <CustomPagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }}
          />
        </Box>
      )}

      {/* Ticket Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Ticket #{selectedTicket.ticketNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTicket.subject}
                  </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <StatusChip label={selectedTicket.status} status={selectedTicket.status} />
                  <IconButton size="small" onClick={() => setDetailOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Grid container>
                {/* Left: Ticket Info */}
                <Grid item xs={12} md={4} sx={{ borderRight: "1px solid #e2e8f0", p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Ticket Details</Typography>
                  
                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary">User</Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: "#3E4094" }}>
                        {selectedTicket.userDetails?.fullName?.charAt(0) || "U"}
                      </Avatar>
                      <Box>
                        <Typography fontSize={14} fontWeight={500}>{selectedTicket.userDetails?.fullName}</Typography>
                        <Typography fontSize={12} color="text.secondary">
                          {selectedTicket.userDetails?.ppskId} | {selectedTicket.userDetails?.phoneNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary">Category</Typography>
                    <Chip label={selectedTicket.category} size="small" sx={{ mt: 0.5, display: "block", width: "fit-content" }} />
                  </Box>

                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary">Priority</Typography>
                    <PriorityChip label={selectedTicket.priority} priority={selectedTicket.priority} size="small" sx={{ mt: 0.5, display: "block", width: "fit-content" }} />
                  </Box>

                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography fontSize={14}>{dayjs(selectedTicket.createdAt).format("DD MMM YYYY, hh:mm A")}</Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary">Update Status</Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedTicket.status}
                    onChange={(e) => handleUpdateStatus(selectedTicket._id, e.target.value)}
                    sx={{ mt: 1 }}
                  >
                    {statuses.filter(s => s !== "All").map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary">Problem</Typography>
                  <Typography fontSize={14} sx={{ mt: 0.5 }}>{selectedTicket.problem}</Typography>

                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography fontSize={14} sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{selectedTicket.description}</Typography>
                  </Box>

                  {selectedTicket.attachments?.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary">Attachments</Typography>
                      <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                        {selectedTicket.attachments.map((att, i) => (
                          <Chip
                            key={i}
                            icon={<AttachFileIcon />}
                            label={`File ${i + 1}`}
                            size="small"
                            component="a"
                            href={BucketURL + att}
                            target="_blank"
                            clickable
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Grid>

                {/* Right: Conversation */}
                <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column", height: 500 }}>
                  <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Conversation ({selectedTicket.replies?.length || 0} messages)
                    </Typography>
                  </Box>

                  {/* Messages */}
                  <Box sx={{ flex: 1, overflow: "auto", p: 2, backgroundColor: "#f8fafc" }}>
                    {selectedTicket.replies?.length > 0 ? (
                      selectedTicket.replies.map((reply, i) => (
                        <Box key={i} mb={2}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5} 
                            sx={{ justifyContent: reply.senderType === "Admin" ? "flex-end" : "flex-start" }}>
                            {reply.senderType !== "Admin" && (
                              <Avatar sx={{ width: 24, height: 24, bgcolor: "#64748b" }}>
                                <PersonIcon sx={{ fontSize: 14 }} />
                              </Avatar>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {reply.senderName} • {dayjs(reply.createdAt).fromNow()}
                            </Typography>
                            {reply.senderType === "Admin" && (
                              <Avatar sx={{ width: 24, height: 24, bgcolor: "#3E4094" }}>
                                <SupportAgentIcon sx={{ fontSize: 14 }} />
                              </Avatar>
                            )}
                          </Box>
                          <MessageBubble isAdmin={reply.senderType === "Admin"}>
                            <Typography fontSize={14}>{reply.message}</Typography>
                          </MessageBubble>
                        </Box>
                      ))
                    ) : (
                      <Box textAlign="center" py={4}>
                        <ChatIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
                        <Typography color="text.secondary" mt={1}>No messages yet</Typography>
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Reply Input */}
                  {!["Closed", "Rejected"].includes(selectedTicket.status) && (
                    <Box sx={{ p: 2, borderTop: "1px solid #e2e8f0", backgroundColor: "#fff" }}>
                      <Box display="flex" gap={1}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Type your reply..."
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && handleSendReply()}
                          multiline
                          maxRows={3}
                        />
                        <IconButton 
                          color="primary" 
                          onClick={handleSendReply}
                          disabled={!replyMessage.trim()}
                          sx={{ bgcolor: "#3E4094", color: "#fff", "&:hover": { bgcolor: "#2d2f6e" }, "&:disabled": { bgcolor: "#e2e8f0" } }}
                        >
                          <SendIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default HelpDesk;
